import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { LoaderCircle } from 'lucide-react';
import { Button as AriaButton } from 'react-aria-components';
import { cx } from './cx.js';
import { useWriteLockOf } from './write-lock.js';
/**
 * Primary is the accent - the one loud element per view, as on the sign-in page. Secondary is
 * outlined, quiet an action in the accent's text colour without a frame, so it never reads as
 * plain text.
 */
const variants = {
    primary: 'bg-accent text-accent-on shadow-ink hover:brightness-110 data-pressed:brightness-95',
    secondary: 'bg-transparent text-text ring-1 ring-hairline ring-inset hover:bg-fill data-pressed:bg-fill-strong',
    quiet: 'bg-transparent text-accent-text hover:bg-accent-soft data-pressed:bg-accent-soft',
    /** Something that cannot be undone: revoking, deleting - with a confirmation around it. */
    danger: 'bg-danger-bg text-danger-fg hover:brightness-95 data-pressed:brightness-90',
    /** An action on an accent surface: the accent's pair swapped, as contrasting as the surface. */
    inverse: 'bg-accent-on text-accent shadow-ink hover:brightness-95 data-pressed:brightness-90',
};
/**
 * The height of every control is 44 px (`md`, the fields alike); `sm` is for dense rows and
 * still 44 px under the finger on touch devices (WCAG 2.5.8, AGENTS.md).
 */
const sizes = {
    lg: 'h-12 px-6 text-base',
    md: 'h-11 px-5 text-[0.9375rem]',
    sm: 'h-9 px-3.5 text-sm max-md:h-11 [@media(pointer:coarse)]:h-11',
};
/**
 * The look of a button, for what navigates and is therefore a link (`<Link>` of the router):
 * it looks like the action it starts, without being a button for assistive technology.
 */
export function buttonClassName(variant = 'primary', size = 'md', className) {
    return cx('inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full font-semibold tracking-[-0.01em] whitespace-nowrap no-underline', 'transition-[background-color,filter] duration-150 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus', 'data-disabled:cursor-not-allowed data-disabled:opacity-40 data-disabled:shadow-none data-pending:cursor-wait', variants[variant], sizes[size], className);
}
/**
 * `isPending` locks the button, sets aria-busy and shows a spinner - the label
 * stays, so the button keeps its width and does not jump under the mouse.
 */
export function Button({ variant = 'primary', size = 'md', writes = false, className, children, ...props }) {
    const locked = useWriteLockOf(writes, props['aria-describedby']);
    return (_jsx(AriaButton, { ...props, ...locked, className: buttonClassName(variant, size, className), children: ({ isPending }) => (_jsxs(_Fragment, { children: [isPending && _jsx(LoaderCircle, { "aria-hidden": true, className: "size-4 animate-spin" }), children] })) }));
}
/**
 * The name of a row that opens it - in a table or a card, as a link to its page or as a button
 * that opens a dialog: both look alike, left-aligned in the accent's text colour.
 */
export const rowLinkClassName = 'rounded-xs text-left font-semibold text-text underline-offset-4 outline-none hover:text-accent-text hover:underline focus-visible:outline-2 focus-visible:outline-focus';
/** A row's name that opens a dialog rather than a page (`rowLinkClassName`). */
export function RowButton({ className, children, ...props }) {
    return (_jsx(AriaButton, { ...props, className: cx(rowLinkClassName, 'cursor-pointer', className), children: children }));
}
const iconSizes = {
    md: 'size-11',
    sm: 'size-9 max-md:size-11 [@media(pointer:coarse)]:size-11',
};
/** A round button that shows only an icon, framed like `secondary` - never stretched. */
export function IconButton({ size = 'md', quiet = false, writes = false, className, children, ...props }) {
    const locked = useWriteLockOf(writes, props['aria-describedby']);
    return (_jsx(AriaButton, { ...props, ...locked, className: cx('grid shrink-0 cursor-pointer place-items-center rounded-full text-sm font-semibold outline-none transition-colors', quiet ? 'text-muted hover:text-text' : 'text-text ring-1 ring-hairline ring-inset', 'hover:bg-fill data-pressed:bg-fill-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus', 'data-disabled:cursor-not-allowed data-disabled:opacity-40', iconSizes[size], className), children: children }));
}
//# sourceMappingURL=button.js.map